import { NodeProps } from '@xyflow/react'
import {
  UniversalSymbolNode,
  UniversalSymbolPreview,
} from 'features/processFlowDiagrams/components/nodes/UniversalSymbolNode'
import { PfdNode } from 'features/processFlowDiagrams/types'
import React from 'react'

import {
  getAllCategories,
  getSymbolsByCategory as getMinimalSymbolsByCategory,
  MinimalSymbol,
} from './minimalSymbols'

// Convert minimal symbols to library format
export interface SymbolDefinition {
  id: string
  name: string
  description?: string
  category: string
  tags: string[]
  component: React.ComponentType<NodeProps<PfdNode>>
  previewComponent: React.ComponentType
}

export interface SymbolCategory {
  id: string
  name: string
  description?: string
  symbols: SymbolDefinition[]
}

// Convert minimal symbol to full symbol definition
const convertMinimalSymbol = (
  minimalSymbol: MinimalSymbol
): SymbolDefinition => ({
  id: minimalSymbol.id,
  name: minimalSymbol.name,
  description: `${minimalSymbol.name} symbol`,
  category: minimalSymbol.category,
  tags: minimalSymbol.tags,
  component: (props: NodeProps<PfdNode>) => (
    <UniversalSymbolNode {...props} symbolId={minimalSymbol.id} />
  ),
  previewComponent: () => (
    <UniversalSymbolPreview symbolId={minimalSymbol.id} />
  ),
})

// Category metadata
const CATEGORY_INFO = {
  basic: { name: 'Basic Shapes', description: 'Basic geometric shapes' },
  instruments: {
    name: 'Instruments',
    description: 'Measurement and control instruments',
  },
  valves: { name: 'Valves', description: 'Flow control valves' },
  pumps: { name: 'Pumps', description: 'Fluid pumping equipment' },
  dryers: { name: 'Dryers', description: 'Drying equipment' },
  vessels: {
    name: 'Vessels',
    description: 'Storage tanks and pressure vessels',
  },
  filters: { name: 'Filters', description: 'Filtration equipment' },
  compressors: {
    name: 'Compressors',
    description: 'Gas compression equipment',
  },
  heat_exchangers: {
    name: 'Heat Exchangers',
    description: 'Heat transfer equipment',
  },
}

// Generate symbol categories from minimal symbols
export const SYMBOL_CATEGORIES: SymbolCategory[] = getAllCategories().map(
  (categoryId) => {
    const categorySymbols = getMinimalSymbolsByCategory(categoryId)
    const categoryInfo = CATEGORY_INFO[
      categoryId as keyof typeof CATEGORY_INFO
    ] || { name: categoryId, description: `${categoryId} equipment` }

    return {
      id: categoryId,
      name: categoryInfo.name,
      description: categoryInfo.description,
      symbols: categorySymbols.map(convertMinimalSymbol),
    }
  }
)

// Helper functions (compatible with existing symbol library interface)
export const getAllSymbols = (): SymbolDefinition[] => {
  return SYMBOL_CATEGORIES.flatMap((category) => category.symbols)
}

export const getSymbolById = (id: string): SymbolDefinition | undefined => {
  return getAllSymbols().find((symbol) => symbol.id === id)
}

export const getSymbolsByCategory = (
  categoryId: string
): SymbolDefinition[] => {
  const category = SYMBOL_CATEGORIES.find((cat) => cat.id === categoryId)
  return category?.symbols || []
}

export const searchSymbols = (query: string): SymbolDefinition[] => {
  if (!query.trim()) return getAllSymbols()

  const lowerQuery = query.toLowerCase()
  return getAllSymbols().filter(
    (symbol) =>
      symbol.name.toLowerCase().includes(lowerQuery) ||
      symbol.description?.toLowerCase().includes(lowerQuery) ||
      symbol.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  )
}

// Create a mapping for ReactFlow nodeTypes
export const createNodeTypesMapping = () => {
  const nodeTypes: Record<string, React.ComponentType<NodeProps<PfdNode>>> = {}

  getAllSymbols().forEach((symbol) => {
    nodeTypes[symbol.id] = symbol.component
  })

  return nodeTypes
}
